import { type ItemType, type PeriodUnit } from '@bbk/contracts/billing';

export {
  PeriodUnit,
  SubscriptionStatus,
  ItemType,
  DiscountApplyOn,
  DiscountDurationType,
  type Subscription,
  type SubscriptionItem,
  type Discount,
} from '@bbk/contracts/billing';
/*------------------------------------------PRICING ENUMS------------------------------------------*/
export enum GroupedBy {
  item_frequency = 'item_frequency',
  item = 'item',
}

export enum BadgeType {
  none = 'none',
  block = 'block',
  full = 'full',
}

export enum QuantityMetaType {
  SLIDER = 'slider',
  NONE = 'none',
}

export enum AddonType {
  mandatory = 'mandatory',
  recommended = 'recommended',
  optional = 'optional',
}

export enum ChargeOnEvent {
  subscription_creation = 'subscription_creation',
  subscription_trial_start = 'subscription_trial_start',
  plan_activation = 'plan_activation',
  subscription_activation = 'subscription_activation',
  contract_termination = 'contract_termination',
  on_demand = 'on_demand',
}

export enum PricingModel {
  flat_fee = 'flat_fee',
  per_unit = 'per_unit',
  tiered = 'tiered',
  volume = 'volume',
  stairstep = 'stairstep',
}

export enum SubscriberCTAAction {
  do_nothing = 'do_nothing',
  redirect_to_url = 'redirect_to_url',
  allow_quantity_upgrades = 'allow_quantity_upgrades',
}

export enum ActionButtonType {
  chargebee_checkout = 'chargebee_checkout',
  custom_url = 'custom_url',
  js_callback = 'js_callback',
}

// Attached Addon type
export type AttachedAddon = {
  billing_system_id: string;
  billing_system_item_id: string;
  billing_system_version: string;
  item_type: 'addon' | 'charge';
  quantity: number;
  type: AddonType;
  charge_on_event: ChargeOnEvent;
  charge_once: boolean;
  // Optional fields enriched from catalog
  customization?: Partial<ItemCustomisations>;
  name?: string;
};

// Price type
//TODO: Remove ID and rely on billing_system_item_price_id/billing_system_id only
export type Price = {
  id: string;
  customization?: Record<string, unknown>;
  active: boolean;
  is_attached: boolean;
  name: string;
  period?: number;
  period_unit?: PeriodUnit;
  amount?: number;
  strikethrough_amount?: number;
  billing_system_amount: number;
  currency: string;
  billing_system: string;
  billing_system_id: string;
  billing_system_item_id: string;
  billing_system_version: string;
  price_variant_id?: string;
  pricing_model: PricingModel;
  trial_period: number | null;
  trial_period_unit: PeriodUnit | null;
  tiers?: Tier[];
  billing_system_tiers?: Tier[];
  differential_prices?: DifferentialPrice[];
  quantity_meta?: QuantityMeta;
};

/*------------------------------------------PRICING SIMPLE TYPES------------------------------------------*/
export type SiteIntegrationItemItemApplicability =
  (typeof SiteIntegrationItemItemApplicability)[keyof typeof SiteIntegrationItemItemApplicability];
export const SiteIntegrationItemItemApplicability = {
  all: 'all',
  restricted: 'restricted',
} as const;

export type Tier = {
  amount: number;
  ending_unit?: number;
  starting_unit: number;
  strikethrough_amount?: number;
};

export type Badge = {
  type: BadgeType;
  text: string;
};

export interface Item {
  customization: ItemCustomisations;
  is_metered: boolean;
  display_order: number;
  period: number | null;
  period_unit: PeriodUnit | null;
  billing_system: string;
  billing_system_id: string;
  billing_system_version: string;
  billing_system_item_id?: string;
  is_attached: boolean;
  product_family: string;
  item_applicability: SiteIntegrationItemItemApplicability;
  applicable_items?: string[];
  attached_addons: AttachedAddon[];
  prices: Price[];
  is_custom_amount_text?: boolean;
  item_type: ItemType;
}

// Addon type
export type Addon = {
  customization: Partial<ItemCustomisations>;
  billing_system: string;
  billing_system_id: string;
  billing_system_version: string;
  billing_system_item_id: string;
  product_family: string;
  is_metered: boolean;
  item_type: ItemType;
  prices: Price[];
  type?: AddonType;
};

// Main PricingTable type
export type PricingTable = {
  id: string;
  name: string;
  grouped_by: GroupedBy;
  frequencies: Frequency[];
  currencies: Currency[];
  variant_ids?: string[];
  items: Item[];
  addons: Addon[];
  customization?: PricingTableCustomisations;
  paywall_config?: PaywallConfig;
};

export enum FrequencyOrientation {
  row = 'row',
  col = 'col',
}

export type PricingTableCustomisations = {
  plan_customization: {
    heading: string;
    sub_heading: string;
    mandatory_addon_title: string;
    recommended_addon_title: string;
    addon_summary_label?: string;
  };
  show_addons_and_charges: boolean;
  hide_price_breakdown: boolean;
  hide_mandatory_addon_prices: boolean;
  hide_plan_customization: boolean;
  frequency_orientation?: FrequencyOrientation; // Display orientation for frequency labels
};

export type Frequency = {
  id: string;
  title: string;
  period: number;
  period_unit: PeriodUnit;
  is_attached: boolean;
  show_monthly_price?: boolean;
  recurring_price_label?: string;
  price_suffix?: string;
  display_order: number;
};

export type Currency = {
  id: string;
  currency_code: string;
  is_attached: boolean;
  display_order: number;
};

export type ItemCustomisations = {
  name: string;
  display_name?: string;
  description?: string;
  features?: string;
  highlight?: boolean;
  badge?: Badge;
  action_button?: ActionButton;
  is_custom_amount_text?: boolean;
  custom_amount_text?: string;
  custom_amount_sub_text?: string;
  quantity_meta?: QuantityMeta;
  info_text?: string;
};

export type QuantityMeta = {
  min: number;
  max: number;
  step: number;
  default_value: number;
  unit_of_measure?: {
    singular?: string;
    plural?: string;
  };
  type: QuantityMetaType;
};

export type ActionButton = {
  type?: ActionButtonType;
  url?: string;
  text?: string;
};

export type PaywallConfig = {
  hide_subscribed_plan?: boolean;
  hide_frequency_switch?: boolean;
  action_button?: SubscriberActionButton;
};

export type SubscriberActionButton = {
  text?: string;
  action: SubscriberCTAAction;
  url?: string;
};

//Create Checkout Response
export type HostedPage = {
  id: string;
  url: string;
  createdAt?: string;
  updatedAt?: string;
  expiresAt?: string;
  state?: string;
  embed?: boolean;
  type?: string;
  layout?: string;
};

//CreateCheckoutRequest prefix -> API request types for Checkout with snake_case properties (what we send to backend)
export type CreateCheckoutRequestVisitor = {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  company?: string;
  custom_fields?: Record<string, any>;
};

export type CreateCheckoutRequestAddress = {
  first_name?: string;
  last_name?: string;
  email?: string;
  company?: string;
  phone?: string;
  line1?: string;
  line2?: string;
  line3?: string;
  city?: string;
  state_code?: string;
  state?: string;
  zip?: string;
  country?: string;
  validation_status?: string;
};

export type CreateCheckoutRequestSubscriptionAttributes = {
  custom_fields?: Record<string, any>;
};

// Base type with common fields for both live and preview checkout
export type BaseCreateCheckoutRequest = {
  visitor_id?: string;
  price_id?: string;
  quantity?: number;
  pricing_page_id?: string;
  pricing_page_version?: number;
  business_entity_id?: string;
  addons?: CreateCheckoutRequestAddon[];
};

// Live checkout request - extends base with session and customer data
export type CreateCheckoutRequest = BaseCreateCheckoutRequest & {
  visitor?: CreateCheckoutRequestVisitor;
  subscription_attributes?: CreateCheckoutRequestSubscriptionAttributes;
  billing_address?: CreateCheckoutRequestAddress;
  shipping_address?: CreateCheckoutRequestAddress;
  session_id?: string;
};

// Preview checkout request - extends base with subscription_id for existing subscriptions
export type CreatePreviewCheckoutRequest = BaseCreateCheckoutRequest & {
  subscription_id?: string;
};

export type CreateCheckoutRequestAddon = {
  price_id?: string;
  quantity?: number;
};

export type CreateCheckoutRequestItem = {
  is_metered?: boolean;
};

export type DifferentialPrice = {
  parent_period?: number;
  parent_period_unit?: PeriodUnit;
  billing_system_id: string;
  billing_system_item_id: string;
  billing_system_version: string;
  amount?: number;
  tiers?: Tier[];
};

export type Visitor = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  customFields?: Record<string, any>;
};

export type SubscriptionAttributes = {
  customFields?: Record<string, any>;
};

export type Address = {
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  phone?: string;
  line1?: string;
  line2?: string;
  line3?: string;
  city?: string;
  stateCode?: string;
  state?: string;
  zip?: string;
  country?: string;
  validationStatus?: 'not_validated' | 'valid' | 'partially_valid' | 'invalid';
};

/*--------------COMPLEX PRICING ITEM TYPES (Pricing Table, Items, Prices, Addons and Attached Items)--------------*/
//Product catalog data: Prefixed with Billing, produced by Sync/PC APIs or useGetProductCatalog hook
//Billing Attached Item type: Used for mapping, use AttachedAddon for external use (Counterpart of AP AttachedSiteIntegrationItem)
export type BillingAttachedItem = {
  billing_system_id: string;
  billing_system_item_id: string;
  billing_system_version: string;
  item_type: ItemType;
  quantity?: number;
  type?: AddonType;
  charge_on_event?: ChargeOnEvent;
  charge_once?: boolean;
};

//Billing Item Type: Use Item when using externally (Counterpart of AP SiteIntegrationItem)
export type BillingItem = {
  id?: string;
  name?: string;
  internal_name?: string;
  description?: string;
  is_metered: boolean;
  billing_system_id: string;
  billing_system: string;
  billing_system_version: string;
  billing_system_item_id?: string;
  product_family: string;
  item_type: ItemType;
  item_applicability: SiteIntegrationItemItemApplicability;
  applicable_items?: string[];
  prices: Price[];
  attached_addons: AttachedAddon[];
};

export type CreatePriceRequest = {
  id: string;
  customization?: Record<string, unknown>;
  active: boolean;
  is_attached: boolean;
  period?: number;
  period_unit?: PeriodUnit;
  amount?: number;
  strikethrough_amount?: number;
  billing_system_amount: number;
  currency: string;
  billing_system: string;
  billing_system_id: string;
  billing_system_item_id: string;
  billing_system_version: string;
  price_variant_id?: string;
  pricing_model: PricingModel;
  trial_period: number | null;
  trial_period_unit: PeriodUnit | null;
  tiers?: Tier[];
  billing_system_tiers?: Tier[];
  differential_prices?: DifferentialPrice[];
};
export type PricingPageRequestBaseFrequencyOrientation =
  (typeof PricingPageRequestBaseFrequencyOrientation)[keyof typeof PricingPageRequestBaseFrequencyOrientation];
export const PricingPageRequestBaseFrequencyOrientation = {
  row: 'row',
  col: 'col',
} as const;

export type SiteIntegrationItem = {
  id: string;
  name: string;
  description?: string;
  is_metered: boolean;
  billing_system: string;
  billing_system_id: string;
  billing_system_version: string;
  billing_system_item_id?: string;
  product_family: string;
  item_type: ItemType;
  item_applicability: SiteIntegrationItemItemApplicability;
  applicable_items?: string[];
  attached_addons: AttachedAddon[];
  prices: CreatePriceRequest[];
};

export type CreateItemRequest = {
  id: string;
  customization: ItemCustomisations;
  is_metered: boolean;
  display_order: number;
  period: number | null;
  period_unit: PeriodUnit | null;
  billing_system: string;
  billing_system_id: string;
  billing_system_version: string;
  billing_system_item_id?: string;
  is_attached: boolean;
  product_family: string;
  item_applicability: SiteIntegrationItemItemApplicability;
  applicable_items?: string[];
  attached_addons: AttachedAddon[];
  prices: CreatePriceRequest[];
  is_custom_amount_text?: boolean;
  item_type: ItemType;
};

export type PriceVariantKey = string | null | undefined;

export type PriceQuantities = {
  [priceId: string]: number;
};
